import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Clock, ExternalLink, Package } from 'lucide-react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';

interface LiveOrder {
  id: string;
  status: string;
  total: number;
  created_at: string;
  customer_name: string;
  phone: string | null;
  order_items: Array<{
    product_name: string;
    quantity: number;
    price: number;
  }>;
}

const statusColors: Record<string, string> = {
  pending: 'bg-warning/10 text-warning border-warning/30',
  confirmed: 'bg-sky/10 text-sky border-sky/30',
  shipped: 'bg-primary/10 text-primary border-primary/30',
  delivered: 'bg-success/10 text-success border-success/30',
  cancelled: 'bg-destructive/10 text-destructive border-destructive/30',
};

const LiveOrders = () => {
  const queryClient = useQueryClient();

  const { data: recentOrders = [], isLoading } = useQuery({
    queryKey: ['recent-orders-live'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('recent_orders_live')
        .select('*')
        .limit(10);

      if (error) throw error;
      return data as LiveOrder[];
    },
  });

  // Set up real-time subscription for new orders
  useEffect(() => {
    const channel = supabase
      .channel('live-orders-updates')
      .on(
        'postgres_changes',
        {
          event: '*', // Listen to all events (INSERT, UPDATE, DELETE)
          schema: 'public',
          table: 'orders',
        },
        () => {
          // Refetch the orders when any change occurs
          queryClient.invalidateQueries({ queryKey: ['recent-orders-live'] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Live Orders
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3 p-3 border rounded-lg animate-pulse">
                <div className="w-8 h-8 bg-muted rounded"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-muted rounded w-3/4"></div>
                  <div className="h-3 bg-muted rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (recentOrders.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Live Orders
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <Package className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
            <p className="text-muted-foreground">No recent orders</p>
            <p className="text-sm text-muted-foreground">New orders will appear here in real-time</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Live Orders ({recentOrders.length})
          </CardTitle>
          <Link to="/admin/orders">
            <Button variant="outline" size="sm" className="gap-2">
              <ExternalLink className="h-3 w-3" />
              View All
            </Button>
          </Link>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {recentOrders.map((order) => {
            const itemCount = order.order_items?.length || 0;
            const firstItem = order.order_items?.[0];
            
            return (
              <div key={order.id} className="flex items-center gap-3 p-3 border rounded-lg hover:bg-muted/30 transition-colors">
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-mono text-xs text-muted-foreground">
                          #{order.id.slice(0, 8)}
                        </span>
                        <Badge className={`text-xs ${statusColors[order.status] || ''}`}>
                          {order.status}
                        </Badge>
                      </div>
                      <h4 className="font-medium text-sm truncate">
                        {order.customer_name}
                      </h4>
                      {order.phone && (
                        <p className="text-xs text-muted-foreground">{order.phone}</p>
                      )}
                    </div>
                    
                    <div className="text-right">
                      <p className="font-medium text-sm">
                        ₹{Number(order.total).toLocaleString('en-IN')}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {format(new Date(order.created_at), 'MMM dd, HH:mm')}
                      </p>
                    </div>
                  </div>
                  
                  <div className="text-xs text-muted-foreground">
                    {firstItem && (
                      <span>
                        {firstItem.product_name} × {firstItem.quantity}
                        {itemCount > 1 && ` +${itemCount - 1} more`}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        
        <div className="mt-4 pt-3 border-t text-center">
          <p className="text-xs text-muted-foreground flex items-center justify-center gap-1">
            <span className="inline-block h-2 w-2 rounded-full bg-success animate-pulse"></span>
            Live updates • Last 7 days
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default LiveOrders;